import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useHookstate } from "@hookstate/core";
import { SITEID_GLOBAL_STATE } from "globalStore/globalStore";
import { useTranslation } from "react-i18next";
import { PEOPLE_SERVICES } from "redux/reduxToolkitServices/_commonServices/peopleServices";
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';
import {
    Header,
    GridContainer,
    LeftSpace
} from "pages/People/PeopleStyled";
import DeleteIcon from "assets/svgIcons/DeleteIcon";
import EditIcon from "assets/svgIcons/EditIcon";
import PlusIcon from "assets/svgIcons/PeopleSubSideNav/PlusIcon";
import SaveIcon from "assets/svgIcons/PeopleSubSideNav/SaveIcon";
import CancelIcon from "assets/svgIcons/PeopleSubSideNav/CancelIcon";
import { Tooltip } from "@mui/material";
import { process } from '@progress/kendo-data-query';
import { featureKey } from "constant";
import AddGroupModal from "./addGroupModal";
import PeopleAlertModal from "../peopleAlertModal";

const GroupPage = React.memo(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getGroupsData, saveGroupData, getPeopleData } = useSelector((state) => state.peopleReducers) || {};
    const { siteSettingsData } = useSelector((state) => state.commonReducers) || {};

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dataState, setDataState] = useState({});
    const [originalRow, setOriginalRow] = useState(null);
    const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
    const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
    const [deleteGroupRow, setDeleteGroupRow] = useState(null);
    const [deleteGroupWarningOpen, setDeleteGroupWarningOpen] = useState(false);

    useEffect(() => {
        if (Object.keys(siteSettingsData).length > 0) {
            if (!siteSettingsData?.Features?.includes(featureKey.resortEdition)) {
                navigate("/dashboard", { replace: true });
            }

            dispatch(PEOPLE_SERVICES.getGroupsBySiteIdService(siteId.get()));
            dispatch(PEOPLE_SERVICES.getPeopleBySiteIdService(siteId.get()));
        }
    }, [siteId.get(), saveGroupData, siteSettingsData, dispatch, navigate]);

    useEffect(() => {
        if (getGroupsData) {
            const updatedGroups = getGroupsData.map((item) => ({
                ...item,
                inEdit: false
            }));
            setGroups(updatedGroups);
            setIsLoading(false);
        }
    }, [getGroupsData]);

    const enableEdit = (dataItem) => {
        setOriginalRow({ ...dataItem });

        const updated = groups.map(item =>
            item.GroupId === dataItem.GroupId
                ? { ...item, inEdit: true }
                : { ...item, inEdit: false }
        );
        setGroups(updated);
    };

    const saveChanges = (dataItem) => {
        const updated = groups.map(item =>
            item.GroupId === dataItem.GroupId
                ? { ...item, inEdit: false }
                : item
        );
        setGroups(updated);
        setOriginalRow(null);

        dispatch(PEOPLE_SERVICES.updateGroupService(siteId.get(), dataItem.GroupId, dataItem));
    };

    const cancelEdit = (dataItem) => {
        const updated = groups.map(item =>
            item.GroupId === dataItem.GroupId
                ? { ...originalRow, inEdit: false }
                : item
        );
        setGroups(updated);
        setOriginalRow(null);
    };

    const handleItemChange = (event) => {
        const updated = groups.map(item =>
            item.GroupId === event.dataItem.GroupId
                ? { ...item, [event.field]: event.value }
                : item
        );
        setGroups(updated);
    };

    const deleteItem = () => {
        const updated = groups.filter(item => item.GroupId !== deleteGroupRow.GroupId);

        setGroups(updated);

        dispatch(PEOPLE_SERVICES.deleteGroupService(siteId.get(), deleteGroupRow.GroupId));
        setDeleteGroupOpen(false);
    }

    const handleDataStateChange = (e) => {
        setDataState(e.dataState);
    };

    const checkIfGroupCanBeDeleted = (dataItem) => {
        let item = getPeopleData.filter(x => x.GroupId == dataItem.GroupId);
        if (item?.length > 0) {
            setDeleteGroupWarningOpen(true);
        } else {
            setDeleteGroupOpen(true); 
            setDeleteGroupRow(dataItem);
        }
    }

    const actionsCell = (props) => {
        const { dataItem } = props;

        if (dataItem.inEdit) {
            return (
                <td>
                    <button
                        title={t("Save")}
                        className="k-button k-primary"
                        style={{ marginRight: 10 }}
                        onClick={() => saveChanges(dataItem)}
                    >
                        <SaveIcon color={"#000"} />
                    </button>
                    <button
                        title={t("Cancel")}
                        className="k-button"
                        onClick={() => cancelEdit(dataItem)}
                    >
                        <CancelIcon color={"#000"} />
                    </button>
                </td>
            );
        }

        return (
            <td>
                <button
                    title={t("Edit_Group")}
                    className="k-button"
                    style={{ marginRight: 10 }}
                    onClick={() => enableEdit(dataItem)}
                >
                    <EditIcon color={"#000"} />
                </button>
                <button
                    title={t("Delete_Group")}
                    className="k-button"
                    onClick={() => checkIfGroupCanBeDeleted(dataItem)}
                >
                    <DeleteIcon color={"#000"} />
                </button>
            </td>
        );
    };

    const processedData = process(groups ?? [], dataState);

    const dataWithEditState = processedData.data.map(row => {
        const original = groups.find(g => g.GroupId === row.GroupId);
        return {
            ...row,
            inEdit: original?.inEdit ?? false
        };
    });

    return (
        <>
            <Header>
                <LeftSpace>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 10 }}>
                        <Tooltip title={t("Add_New_Group")}>
                            <button
                                onClick={() => setAddGroupModalOpen(true)}
                                style={{
                                    backgroundColor: "var(--theme-background)",
                                    padding: 5,
                                    border: "1px solid #fff",
                                    cursor: 'pointer'
                                }}
                            >
                                <PlusIcon style={{ verticalAlign: 'middle' }} color={"#fff"} width={16} height={16} />
                                <span style={{ verticalAlign: 'middle', color: "#fff", fontWeight: "bold", marginLeft: 5 }}>
                                    {t("Add_New_Group")}
                                </span>
                            </button>
                        </Tooltip>
                    </div>
                </LeftSpace>
            </Header>

            <GridContainer>
                <Grid
                    data={dataWithEditState}
                    pageable
                    sortable
                    filterable
                    loading={isLoading}
                    {...dataState}
                    onDataStateChange={handleDataStateChange}
                    editField="inEdit"
                    onItemChange={handleItemChange}
                >
                    <Column
                        title=""
                        cell={actionsCell}
                        filterable={false}
                        width={100}
                    />
                    <Column field="GroupName" title={t("Group_Name")} editable={true} editor="text" />
                </Grid>
            </GridContainer>

            <AddGroupModal 
                setIsLoading={setIsLoading}
                addGroupModalOpen={addGroupModalOpen}
                setAddGroupModalOpen={setAddGroupModalOpen} />

            <PeopleAlertModal
                openPeopleAlertModalOpen={deleteGroupOpen} 
                yesCallbackFunction={() => deleteItem()}
                noCallbackFunction={() => setDeleteGroupOpen(false)}
                title={t("Confirm_delete")}
                content={`${t("Are_You_Sure_Delete_Group")} ${deleteGroupRow?.GroupName} ?`} 
                loading={false}
                isMessageOnly={false} />

            <PeopleAlertModal
                openPeopleAlertModalOpen={deleteGroupWarningOpen} 
                title={t("Warning")}
                yesCallbackFunction={() => {}}
                noCallbackFunction={() => setDeleteGroupWarningOpen(false)}
                content={t("Group_In_Use")} 
                loading={false}
                isMessageOnly={true} />
        </>
    );
});

export default GroupPage;